import React, { useContext, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import { useDisconnectBrokerConnection, useUpdateAppSettings } from '../../hooks/useBackend';
import { formatBrokerConnectionLabel, isBrokerLinked } from '../../utils/brokerConnection';

export default function DisconnectBrokerModal({ show, onHide, connection, onDisconnected }) {
  const { colors } = useTheme();
  const { user, refreshUserSettings, updateUserSettings } = useContext(AuthContext);
  const { clearTradingStateOnDisconnect, setSelectedAccount, accounts } = useContext(TradingContext);
  const { disconnectConnection } = useDisconnectBrokerConnection();
  const { updateAppSettings } = useUpdateAppSettings();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleDisconnect = async () => {
    const connId = connection?.id;
    if (!connId || !user?.token) return;
    setLoading(true);
    setError(null);
    try {
      const result = await disconnectConnection(user.token, connId);
      if (result?.error) {
        setError(result.error);
        return;
      }

      const remaining =
        (user.brokerConnections || []).filter((c) => String(c.id) !== String(connId));
      const stillLinked = remaining.length > 0;
      // Persist cleared selection on the server BEFORE refresh (avoids stale SIM reappearing).
      try {
        await updateAppSettings(user.token, { selectedAccountId: undefined });
      } catch (_) {
        // best-effort — server disconnect also clears selectedAccountId
      }

      if (stillLinked) {
        const nextAccount =
          (accounts || []).find(
            (a) =>
              a.ct_connection_id &&
              remaining.some((c) => String(c.connection_id) === String(a.ct_connection_id))
          ) ||
          (accounts || []).find(
            (a) =>
              a.broker_name &&
              remaining.some(
                (c) => String(c.broker_name || '').toUpperCase() === String(a.broker_name).toUpperCase()
              )
          ) ||
          null;

        updateUserSettings?.({
          hasBrokerConnection: true,
          brokerConnections: remaining,
          activeBrokerConnection: remaining[0] || null,
        });
        clearTradingStateOnDisconnect?.();
        if (nextAccount) {
          setSelectedAccount?.(nextAccount);
        }
      } else {
        clearTradingStateOnDisconnect?.();
        updateUserSettings?.({
          hasBrokerConnection: false,
          hasTradeStationCredentials: false,
          brokerConnections: [],
          activeBrokerConnection: null,
        });
      }

      await refreshUserSettings?.();
      onDisconnected?.();
      onHide?.();
    } catch (e) {
      setError(e?.message || 'Failed to disconnect broker.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton style={{ background: colors.bgSecondary, borderColor: colors.border }}>
        <Modal.Title style={{ color: colors.text, fontSize: 18 }}>Disconnect Broker</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: colors.bgSecondary, color: colors.text }}>
        {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <p style={{ marginBottom: 8 }}>
          This will unlink <strong>{formatBrokerConnectionLabel(connection) || 'your broker'}</strong> and stop live trading and streaming data for that broker.
        </p>
        <p style={{ marginBottom: 0, fontSize: 13, color: colors.textMuted }}>
          {isBrokerLinked(user) && (user.brokerConnections || []).length > 1
            ? 'Other linked brokers stay connected.'
            : 'Your Precision Trader account is not affected.'}
        </p>
      </Modal.Body>
      <Modal.Footer style={{ background: colors.bgSecondary, borderColor: colors.border }}>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDisconnect} disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" /> : 'Disconnect'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
